import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Divider,
  ListItemButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  LogoutRounded,
  PersonOutlineRounded,
  TranslateRounded,
  UnfoldMoreRounded,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import { useUser } from "../../Query/useUsers";
import { useCurrentWorkspace } from "../../Query/useWorkspace";
import { useLanguageSwitch } from "../Common/LanguageButton";
import { sidebarMenuPaperSx } from "./DashboardMenu";

interface Props {
  collapsed: boolean;
  onNavigate?: () => void;
  onLogout: () => void;
}

const SidebarProfile: FC<Props> = ({ collapsed, onNavigate, onLogout }) => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { data: workspaceData } = useCurrentWorkspace();
  const { current, flagSrc, switchLanguage } = useLanguageSwitch();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const fullName =
    [user?.name, user?.surname].filter(Boolean).join(" ") || user?.email || "";
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();
  const workspaceName = user?.workspace?.name ?? null;
  const workspaceIcon = workspaceData?.workspace?.icon_image
    ? `data:image/png;base64,${workspaceData.workspace.icon_image}`
    : null;

  const closeMenu = () => setAnchor(null);

  const goToProfile = () => {
    closeMenu();
    onNavigate?.();
    navigate("/profile");
  };

  const logout = () => {
    closeMenu();
    onNavigate?.();
    onLogout();
  };

  const avatar = (size: number) => (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.45,
        fontWeight: 700,
        backgroundColor: color.primary_dark,
        color: color.white,
      }}
    >
      {initial}
    </Avatar>
  );

  const trigger = (
    <ListItemButton
      onClick={(event) => setAnchor(event.currentTarget)}
      aria-label={t("dashboard.accountMenu")}
      aria-haspopup="menu"
      aria-expanded={Boolean(anchor)}
      sx={{
        borderRadius: "10px",
        minHeight: 48,
        px: collapsed ? 0 : 1,
        gap: 1.25,
        justifyContent: collapsed ? "center" : "flex-start",
        "&:hover": { backgroundColor: "rgba(3, 46, 77, 0.06)" },
      }}
    >
      {avatar(32)}
      {!collapsed && (
        <>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              sx={{
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "18px",
                color: color.primary_dark,
              }}
            >
              {fullName}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: 12, lineHeight: "16px", color: color.gray500 }}
            >
              {workspaceName ?? user?.email}
            </Typography>
          </Box>
          <UnfoldMoreRounded sx={{ fontSize: 18, color: color.gray400 }} />
        </>
      )}
    </ListItemButton>
  );

  return (
    <>
      {collapsed ? (
        <Tooltip title={fullName} placement="right" arrow>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={closeMenu}
        anchorOrigin={
          collapsed
            ? { vertical: "bottom", horizontal: "right" }
            : { vertical: "top", horizontal: "left" }
        }
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              ...sidebarMenuPaperSx(),
              width: 260,
              ...(collapsed ? { ml: 1.5 } : { mt: -1 }),
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {avatar(40)}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: color.primary_dark,
                }}
              >
                {fullName}
              </Typography>
              <Typography noWrap sx={{ fontSize: 12, color: color.gray500 }}>
                {user?.email}
              </Typography>
            </Box>
          </Box>
          {workspaceName && (
            <Box
              sx={{
                mt: 1.5,
                px: 1,
                py: 0.75,
                borderRadius: "8px",
                backgroundColor: color.gray50,
                border: `1px solid ${color.gray200}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {workspaceIcon && (
                <img
                  src={workspaceIcon}
                  alt=""
                  style={{ width: 22, height: 22, objectFit: "contain" }}
                />
              )}
              <Typography
                noWrap
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: color.primary_dark,
                }}
              >
                {workspaceName}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 0.5, borderColor: color.gray200 }} />
        <MenuItem onClick={goToProfile} sx={{ gap: 1.5 }}>
          <PersonOutlineRounded sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 14, color: color.primary_dark }}>
            {t("dashboard.myProfile")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={switchLanguage} sx={{ gap: 1.5 }}>
          <TranslateRounded sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 14, color: color.primary_dark, flex: 1 }}>
            {t("dashboard.language")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 12,
              fontWeight: 700,
              color: color.gray500,
            }}
          >
            <img
              src={flagSrc}
              alt=""
              width={18}
              height={18}
              style={{ display: "block" }}
            />
            {current.toUpperCase()}
          </Box>
        </MenuItem>
        <Divider sx={{ my: 0.5, borderColor: color.gray200 }} />
        <MenuItem onClick={logout} sx={{ gap: 1.5 }}>
          <LogoutRounded sx={{ fontSize: 20, color: color.red600 }} />
          <Typography sx={{ fontSize: 14, color: color.red600 }}>
            {t("dashboard.logout")}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default SidebarProfile;
